import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { NAVIGATION_KEYS, NS } from '../../../../i18n/constants/keys';
import { ChevronDownIcon, ChevronRightIcon, XIcon } from '../../../../icons/animated';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import {
  PrivateSidebarItems,
  PublicSidebarItems,
} from '../../../layout/Sidebar/MenuItems';
import { IMobileMenuProps } from '../../interfaces';
import { MobileNotifications } from '../../MobileNotifications';
import { MobileProfile } from '../../MobileProfile';

export const MobileMenuDrawer = ({
  isOpen,
  onClose,
  openDropdown,
  setOpenDropdown,
}: IMobileMenuProps) => {
  const { t } = useTranslation(NS.navigation);
  const location = useLocation();

  const handleDropdownToggle = (labelKey: string) => {
    setOpenDropdown(openDropdown === labelKey ? null : labelKey);
  };

  const handleLinkClick = () => {
    onClose();
  };

  const items = [...PrivateSidebarItems, ...PublicSidebarItems];

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      BackdropProps={{
        sx: {
          top: 'var(--nav-height, 64px)',
          backdropFilter: 'blur(3px)',
          bgcolor: 'rgba(15, 23, 42, 0.3)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 380 },
          maxWidth: '100vw',
          top: 'var(--nav-height, 64px)',
          height: 'calc(100vh - var(--nav-height, 64px))',
          bgcolor: '#f8fafc',
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          borderLeft: '1px solid #e2e8f0',
          boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {/* Navigation Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          p: 1.5,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            mb: 1,
            px: 1,
            fontWeight: 800,
            fontSize: '0.68rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {t(NAVIGATION_KEYS.mobileMenu.navigation)}
        </Typography>

        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item) => {
            const hasSubItems = Boolean(item.subItems?.length);
            const isActive =
              item.to && item.to !== '' && location.pathname === item.to;
            const isDropdownOpen = openDropdown === item.labelKey;
            const itemLabel = t(item.labelKey);

            return (
              <PermissionGate
                key={item.labelKey}
                requiredPermissions={item.assignedPermissions}
                matchAnyPermission={item.matchAnyPermission}
              >
                <Box>
                  {hasSubItems ? (
                    <ListItemButton
                      onClick={() => handleDropdownToggle(item.labelKey)}
                      sx={{
                        minHeight: 44,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: isDropdownOpen
                          ? (theme) => alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        color: isDropdownOpen ? 'primary.main' : 'text.primary',
                        border: '1px solid',
                        borderColor: isDropdownOpen
                          ? (theme) => alpha(theme.palette.primary.main, 0.25)
                          : 'transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: isDropdownOpen ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={itemLabel}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isDropdownOpen ? 700 : 500,
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: isDropdownOpen ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {isDropdownOpen ? (
                          <ChevronDownIcon size={16} />
                        ) : (
                          <ChevronRightIcon size={16} />
                        )}
                      </Box>
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      component={Link as any}
                      to={item.to}
                      onClick={handleLinkClick}
                      sx={{
                        minHeight: 44,
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: isActive
                          ? (theme) => alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                        color: isActive ? 'primary.main' : 'text.primary',
                        border: '1px solid',
                        borderColor: isActive
                          ? (theme) => alpha(theme.palette.primary.main, 0.25)
                          : 'transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: isActive ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={itemLabel}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive ? 700 : 500,
                        }}
                      />
                    </ListItemButton>
                  )}

                  {/* Submenu Accordion */}
                  {hasSubItems && (
                    <Collapse in={isDropdownOpen} timeout='auto' unmountOnExit>
                      <List
                        disablePadding
                        sx={{
                          pl: 2,
                          pr: 0.5,
                          py: 0.5,
                          mt: 0.5,
                          ml: 2,
                          borderLeft: '2px solid',
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        {item.subItems?.map((subItem) => {
                          const isSubActive = Boolean(subItem.to && location.pathname === subItem.to);
                          const subLabel = t(subItem.labelKey);

                          const buttonContent = (
                            <ListItemButton
                              component={Link as any}
                              to={subItem.to || '#'}
                              onClick={handleLinkClick}
                              sx={{
                                minHeight: 38,
                                borderRadius: 1.5,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: isSubActive
                                  ? (theme) => alpha(theme.palette.primary.main, 0.1)
                                  : 'transparent',
                                color: isSubActive ? 'primary.main' : 'text.secondary',
                                '&:hover': {
                                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                  color: 'primary.main',
                                },
                              }}
                            >
                              <ListItemText
                                primary={subLabel}
                                primaryTypographyProps={{
                                  fontSize: '0.8rem',
                                  fontWeight: isSubActive ? 700 : 500,
                                }}
                              />
                            </ListItemButton>
                          );

                          return subItem.permissionGate ? (
                            <PermissionGate
                              key={subItem.labelKey}
                              requiredPermissions={subItem.permissionGate.requiredPermissions}
                              matchAnyPermission={subItem.permissionGate.matchAnyPermission}
                            >
                              {buttonContent}
                            </PermissionGate>
                          ) : (
                            <React.Fragment key={subItem.labelKey}>
                              {buttonContent}
                            </React.Fragment>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              </PermissionGate>
            );
          })}
        </List>
      </Paper>

      {/* Notifications Section */}
      <Box>
        <MobileNotifications />
      </Box>

      {/* Profile & Session Section */}
      <Box>
        <MobileProfile />
      </Box>
    </Drawer>
  );
};
